import { Checkbox, Divider, Group, Stack, Text } from "@mantine/core";
import cx from "clsx";
import { type FC, Fragment } from "react";

import classes from "../EvaluationPage.module.css";

/**
 * Minimal shape shared by the evaluation-results (`EvaluationDetailsBody`) and stored training-session
 * (`TrainingSessionEvaluationItem`) behavior records, so this list renders either without mapping.
 */
export interface BehaviorChecklistItem {
    behavior?: string;
    behaviorPresent?: number;
}

export interface BehaviorsChecklistProps {
    behaviors: BehaviorChecklistItem[];
    /** Highlights the active row (page flow). Omit for a static, read-only list (overlay flow). */
    activeBehaviorIndex?: number;
    /** When provided, rows become clickable and report their index. Omit for a read-only list. */
    onClickBehavior?: (index: number) => void;
}

/**
 * The behaviors list itself — numbered rows with a read-only pass/fail checkbox and dividers — with no
 * surrounding container. `EvaluationBehaviorsList` wraps this in its Paper/ScrollArea for the evaluation
 * page; `EvaluationResultsOverlay` renders it inside its own section. Extracted so both reuse the rows.
 */
export const BehaviorsChecklist: FC<BehaviorsChecklistProps> = ({
    behaviors,
    activeBehaviorIndex,
    onClickBehavior,
}) => {
    if (!behaviors.length) {
        return (
            <Text c="dimmed" size="sm">
                No behaviors to review
            </Text>
        );
    }

    return (
        <Stack gap={0}>
            {behaviors.map((behavior, index) => (
                <Fragment key={`${behavior.behavior}-${index}`}>
                    {index !== 0 && <Divider />}
                    <Group
                        p="0.625rem"
                        gap="0.75rem"
                        wrap="nowrap"
                        className={cx({
                            [classes.behaviorChecklistItem]: !!onClickBehavior,
                            [classes.activeChecklistItem]: activeBehaviorIndex === index,
                        })}
                        onClick={onClickBehavior ? () => onClickBehavior(index) : undefined}
                        data-testid={`evaluation-behavior-item-${index}`}
                    >
                        <Text size="0.875rem" fw="500" c="var(--mantine-color-gray-0)">
                            {index + 1}.
                        </Text>
                        <Text
                            size="0.875rem"
                            flex="1"
                            fw="500"
                            c="var(--mantine-color-gray-0)"
                            className={classes.checklistContent}
                        >
                            {behavior.behavior}
                        </Text>
                        <Checkbox checked={behavior.behaviorPresent === 1} readOnly={true} size="xs" />
                    </Group>
                </Fragment>
            ))}
        </Stack>
    );
};

export default BehaviorsChecklist;
